import React, { useCallback, useEffect, useState } from 'react';
import { isEmpty, isEqual } from 'lodash';
import { useFiltersGlobalState } from './filtersGlobalState';

const listContainsFilter = (list, filter) => {
    return list.some(item => isEqual(item, filter));
};

export const useDropdownMenuFilters = (props, setSelectedFiltersWithSubFilters, filters) => {
    const [selectedFiltersWithSubFiltersMap] = useFiltersGlobalState();
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [filteredChildren, setFilteredChildren] = useState([]);

    const setInitialFilter = useCallback(
        initialFilter => {
            if (isEmpty(initialFilter)) {
                return;
            }

            setSelectedFiltersWithSubFilters(initialFilter);
        },
        [setSelectedFiltersWithSubFilters]
    );

    // Sets the list of items in the dropdown that match the selected filters
    useEffect(() => {
        setFilteredChildren(prev =>
            React.Children.toArray(props.children).filter(child => {
                if (child.props.children === 'No options') {
                    return true;
                }
                const childData = child.props.data;
                return Object.values(selectedFiltersWithSubFiltersMap).every(filter => {
                    const { selectedFilter, selectedSubFilters } = filter;

                    if (!selectedSubFilters || selectedSubFilters.length === 0) {
                        return prev.contains(child);
                    }

                    return selectedSubFilters
                        .map(subFilter => subFilter.filterValue)
                        .includes(childData[selectedFilter.fieldName]);
                });
            })
        );
    }, [props.children, selectedFiltersWithSubFiltersMap]);

    const onSelectFilter = useCallback(value => {
        setSelectedFilter(value);
    }, []);

    const onSelectSubFilter = useCallback(
        value => {
            const prevSelectedSubFilters =
                selectedFiltersWithSubFiltersMap[selectedFilter.name]?.selectedSubFilters || [];
            const updatedSubFilters = listContainsFilter(prevSelectedSubFilters, value)
                ? prevSelectedSubFilters.filter(item => !isEqual(item, value))
                : [...prevSelectedSubFilters, value];

            setSelectedFiltersWithSubFilters({ selectedFilter, selectedSubFilters: updatedSubFilters });
        },
        [selectedFilter, selectedFiltersWithSubFiltersMap, setSelectedFiltersWithSubFilters]
    );

    const onclickSelectedFilter = useCallback(filter => {
        setSelectedFilter(filter);
    }, []);

    const onRemoveSelectedFilter = useCallback(
        (filter, subFilter) => {
            const prevSelectedSubFilters = selectedFiltersWithSubFiltersMap[filter.name]?.selectedSubFilters || [];
            const updatedSubFilters = prevSelectedSubFilters.includes(subFilter)
                ? prevSelectedSubFilters.filter(item => item !== subFilter)
                : [...prevSelectedSubFilters, subFilter];

            setSelectedFiltersWithSubFilters({ selectedFilter: filter, selectedSubFilters: updatedSubFilters });
        },
        [selectedFiltersWithSubFiltersMap, setSelectedFiltersWithSubFilters]
    );

    const getValuesForSelectedFilter = selectedFilter => {
        const filter = filters.find(filter => filter.name === selectedFilter.name);
        return filter ? filter.subFilters : [];
    };

    const getSubFiltersForSelectedFilter = selectedFilter => {
        return selectedFiltersWithSubFiltersMap[selectedFilter.name]?.selectedSubFilters || [];
    };

    const getSelectedFiltersFormMap = () => {
        return Object.values(selectedFiltersWithSubFiltersMap);
    };

    return {
        selectedFilter,
        onSelectFilter,
        setSelectedFilter,
        getValuesForSelectedFilter,
        onSelectSubFilter,
        getSubFiltersForSelectedFilter,
        getSelectedFiltersFormMap,
        onclickSelectedFilter,
        onRemoveSelectedFilter,
        filteredChildren,
        setInitialFilter,
    };
};
